import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * @typedef {object} CartInvoiceRefs
 * @property {HTMLElement} cartInvoiceError
 * @property {HTMLElement} cartInvoiceErrorMessage
 * @property {HTMLElement} cartInvoiceSuccess
 */
class CartInvoiceComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#openDisclosureIfValid();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
  }

  #openDisclosureIfValid() {
    // Check if VAT is already validated (from localStorage)
    const isValid = localStorage.getItem('invoice-requested-status') === 'yes';
    if (!isValid) return;

    const details = this.closest('accordion-custom')?.querySelector('details');
    if (details) details.open = true;
  }

  /**
   * @param {Event} event
   */
  validateVat = async (event) => {
    event.preventDefault();
    if (!(event.target instanceof HTMLFormElement)) return;

    const form = event.target;
    const input = form.querySelector('input[name="vat"]');
    if (!(input instanceof HTMLInputElement)) return;

    const vat = input.value.trim()?.replace(/\s+/g, '').toUpperCase();
    if (!vat) {
      this.#toggleMessage('error', 'Please enter a VAT number');
      return;
    }

    this.#setFormDisabled(form, true);

    try {
      const isValid = await this.#validateVatNumber(vat);
      if (isValid) {
        await this.#updateCart(vat);
        this.#toggleMessage('success');
        this.#updateDisclosure(true);
      } else {
        this.#toggleMessage('error', 'Invalid VAT number');
        await this.#clearCart();
      }
    } catch (error) {
      this.#toggleMessage('error', error.message || 'Validation failed');
      await this.#clearCart();
    } finally {
      this.#setFormDisabled(form, false);
    }
  };

  /**
   * @param {string} vatNumber
   */
  #validateVatNumber = async (vatNumber) => {
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const res = await fetch(`https://api.vatcomply.com/vat?vat_number=${encodeURIComponent(vatNumber)}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        signal: controller.signal,
      });

      if (!res.ok) return false;
      const data = await res.json();
      return data?.valid === true || data?.isValid === true;
    } catch {
      return false;
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * @param {string} vatNumber
   */
  #updateCart = async (vatNumber) => {
    localStorage.setItem('invoice-requested-status', 'yes');
    localStorage.setItem('invoice-vat-value', vatNumber);

    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: { 'Invoice requested': 'yes', 'VAT Number': vatNumber },
      }),
    });

    const data = await fetch(Theme.routes.cart_update_url, config).then((r) => r.json());
    if (data.sections?.[this.sectionId]) morphSection(this.sectionId, data.sections[this.sectionId]);
  };

  #clearCart = async () => {
    localStorage.setItem('invoice-requested-status', 'no');
    localStorage.setItem('invoice-vat-value', '');

    const config = fetchConfig('json', {
      body: JSON.stringify({
        attributes: { 'Invoice requested': '', 'VAT Number': '' },
      }),
    });

    const data = await fetch(Theme.routes.cart_update_url, config).then((r) => r.json());
    if (data.sections?.[this.sectionId]) morphSection(this.sectionId, data.sections[this.sectionId]);
  };

  /**
   * @param {'error' | 'success'} type
   * @param {string} message
   */
  #toggleMessage(type, message = '') {
    const error = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceError);
    const errorMsg = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceErrorMessage);
    const success = /** @type {HTMLElement | undefined} */ (this.refs.cartInvoiceSuccess);

    error?.classList.toggle('hidden', type === 'success');
    errorMsg?.classList.toggle('hidden', type === 'success');
    success?.classList.toggle('hidden', type === 'error');

    if (type === 'error' && errorMsg) errorMsg.textContent = message;
  }

  /**
   * @param {boolean} expand
   */
  #updateDisclosure(expand) {
    const details = this.closest('accordion-custom')?.querySelector('details');
    if (details) details.open = !!expand;
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} disabled
   */
  #setFormDisabled(form, disabled) {
    form.querySelectorAll('input, button').forEach(/** @param {Element} el */(el) => el.toggleAttribute('disabled', disabled));
  }

  get sectionId() {
    return this.dataset.sectionId || '';
  }
}

if (!customElements.get('cart-invoice-component')) {
  customElements.define('cart-invoice-component', CartInvoiceComponent);
}
